import { Injectable, computed, signal } from '@angular/core';
import {
    BasicDetailsState,
    EducationalDetailsState,
    ProfessionalDetailsState,
    StudentApplicationFormState,
} from '@lib/interfaces/student-application-state-interfaces';
@Injectable({
    providedIn: 'root',
})
export class StudentApplicationStateService {
    private readonly initialState: StudentApplicationFormState = {
        enrollmentId: '',
        basicDetails: {
            courseId: '',
            name: '',
            email: '',
            mobileNumber: '',
            gender: '',
            mailingAddress: '',
            accommodationRequired: false,
            feeCategory: '',
        },
        educationalBackground: {
            highestEducation: '',
            fieldOfStudy: '',
            institutionName: '',
            yearOfCompletion: '',
            enrollmentMarksheet: null,
        },
        professionalBackground: {
            currentOrganisation: '',
            designation: '',
            workExperience: '',
            enrollmentDocument: null,
        },
        currentStep: 1,
        completedSteps: [],
        isSubmitted: false,
    };

    // State signals
    private readonly state = signal<StudentApplicationFormState>(this.initialState);

    // Computed signals for each step
    readonly basicDetails = computed(() => this.state().basicDetails);
    readonly educationalDetails = computed(() => this.state().educationalBackground);
    readonly professionalDetails = computed(() => this.state().professionalBackground);
    readonly currentStep = computed(() => this.state().currentStep);
    readonly completedSteps = computed(() => this.state().completedSteps);
    readonly isSubmitted = computed(() => this.state().isSubmitted);

    // State update methods
    updateBasicDetails(basicDetails: Partial<BasicDetailsState>) {
        this.state.update((state) => ({
            ...state,
            basicDetails: { ...state.basicDetails, ...basicDetails },
        }));
    }

    updateEducationalDetails(educationalDetails: Partial<EducationalDetailsState>) {
        this.state.update((state) => ({
            ...state,
            educationalBackground: { ...state.educationalBackground, ...educationalDetails },
        }));
    }

    updateProfessionalDetails(professionalDetails: Partial<ProfessionalDetailsState>) {
        this.state.update((state) => ({
            ...state,
            professionalBackground: { ...state.professionalBackground, ...professionalDetails },
        }));
    }

    setCurrentStep(step: number) {
        this.state.update((state) => ({
            ...state,
            currentStep: step,
        }));
    }

    addCompletedStep(step: number) {
        this.state.update((state) => ({
            ...state,
            completedSteps: [...new Set([...state.completedSteps, step])],
        }));
    }

    setSubmitted(isSubmitted: boolean) {
        this.state.update((state) => ({
            ...state,
            isSubmitted,
        }));
    }

    resetState() {
        this.state.set(this.initialState);
    }

    // Utility methods
    isStepComplete(step: number): boolean {
        return this.completedSteps().includes(step);
    }

    getFormState(): StudentApplicationFormState {
        return this.state();
    }
}
